import React, { useState } from "react";
import { Lock, Loader2, KeyRound, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { adminApi } from "./api";
import { useToast } from "../hooks/use-toast";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setShowCurrent(false);
    setShowNext(false);
    setErr("");
    setDone(false);
    setSubmitting(false);
  };

  const close = (val) => {
    onOpenChange(val);
    if (!val) reset();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 6) {
      setErr("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("New password and confirmation do not match.");
      return;
    }
    if (current === next) {
      setErr("New password must differ from the current one.");
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.changePassword(current, next);
      setDone(true);
      toast({ title: "Password updated", description: "Use the new password next time you sign in." });
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to update password");
    } finally {
      setSubmitting(false);
    }
  };

  const strength = (() => {
    if (!next) return null;
    let score = 0;
    if (next.length >= 8) score++;
    if (/[A-Z]/.test(next)) score++;
    if (/[0-9]/.test(next)) score++;
    if (/[^A-Za-z0-9]/.test(next)) score++;
    return score;
  })();

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-orange-500" /> Change Admin Password
          </DialogTitle>
          <DialogDescription>
            Choose a strong password. You will keep your current session.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="mt-4 font-bold text-slate-900">Password updated</div>
            <p className="text-sm text-slate-600 mt-1">
              Your new password is active. Remember it for next login.
            </p>
            <Button
              onClick={() => close(false)}
              className="mt-5 bg-[#0a4f47] hover:bg-[#0e6e63] text-white"
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold text-slate-700">Current password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={showCurrent ? "text" : "password"}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  className="h-11 pl-10 pr-10"
                  placeholder="Enter current password"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label="Toggle visibility"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700">New password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={showNext ? "text" : "password"}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  className="h-11 pl-10 pr-10"
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNext(!showNext)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  aria-label="Toggle visibility"
                >
                  {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {strength !== null && (
                <div className="mt-2 flex items-center gap-1.5">
                  {[1, 2, 3, 4].map((lvl) => (
                    <div
                      key={lvl}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        lvl <= strength
                          ? strength >= 3
                            ? "bg-emerald-500"
                            : strength === 2
                            ? "bg-orange-400"
                            : "bg-red-400"
                          : "bg-slate-200"
                      }`}
                    />
                  ))}
                  <span className="text-xs text-slate-500 ml-2">
                    {strength >= 3 ? "Strong" : strength === 2 ? "Okay" : "Weak"}
                  </span>
                </div>
              )}
              <p className="text-xs text-slate-400 mt-1.5">
                Tip: use 8+ characters with a mix of upper, number, and a symbol.
              </p>
            </div>

            <div>
              <Label className="text-sm font-semibold text-slate-700">Confirm new password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type={showNext ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="h-11 pl-10"
                  placeholder="Re-enter new password"
                  required
                  autoComplete="new-password"
                />
              </div>
            </div>

            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
                {err}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => close(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#0a4f47] hover:bg-[#0e6e63] text-white"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Updating
                  </span>
                ) : (
                  "Update password"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
